import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Typography,
  Stack,
  IconButton,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

export interface Produto {
  id: string;
  nomeRazao: string;
  documento: string;
  valor: string;
  valorNumerico: number;
}

export interface SecaoAquisicao {
  titulo: string;
  produtosCnpj: Produto[];
  produtosCpf: Produto[];
}

const SectionTable: React.FC<{
  tipo: 'cnpj' | 'cpf';
  secaoIndex: number;
  secao: SecaoAquisicao;
  novosProdutos: any[];
  handleInputChange: (secaoIndex: number, tipo: 'cnpj' | 'cpf', field: string, value: string) => void;
  handleClearFields: (secaoIndex: number, tipo: 'cnpj' | 'cpf') => void;
  handleAddProduto: (secaoIndex: number, tipo: 'cnpj' | 'cpf') => void;
  handleRemoveProduto: (secaoIndex: number, tipo: 'cnpj' | 'cpf', produtoId: string) => void;
  validarCPF?: (v: string) => boolean;
  validarCNPJ?: (v: string) => boolean;
}> = ({
  tipo,
  secaoIndex,
  secao,
  novosProdutos,
  handleInputChange,
  handleClearFields,
  handleAddProduto,
  handleRemoveProduto,
  validarCPF,
  validarCNPJ,
}) => {
    const isCnpj = tipo === 'cnpj';
    const title = `${secaoIndex + 1}.${isCnpj ? '1' : '2'} Valores via ${isCnpj ? 'CNPJ' : 'CPF'}`;
    const docPlaceholder = isCnpj ? '00.000.000/0000-00' : '000.000.000-00';
    const maxLength = isCnpj ? 18 : 14;
    const produtosList = isCnpj ? secao.produtosCnpj : secao.produtosCpf;
    const novo = novosProdutos[secaoIndex][tipo];
    const validar = isCnpj ? (validarCNPJ ?? (() => true)) : (validarCPF ?? (() => true));
    const invalidMsg = isCnpj ? 'CNPJ inválido' : 'CPF inválido';

    return (
      <>
        <Typography variant="h6" sx={{ mb: 2 }}>{title}</Typography>
        <TableContainer component={Paper} sx={isCnpj ? { mb: 3 } : undefined}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome/Razão Social</TableCell>
                <TableCell>{isCnpj ? 'CNPJ' : 'CPF'}</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell align="right" width="250px">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={novo.nomeRazao}
                    onChange={(e) => handleInputChange(secaoIndex, tipo, 'nomeRazao', e.target.value.toLocaleUpperCase())}
                    placeholder="Digite o nome/razão social"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={novo.documento}
                    onChange={(e) => handleInputChange(secaoIndex, tipo, 'documento', e.target.value)}
                    placeholder={docPlaceholder}
                    inputProps={{ maxLength }}
                    error={!!novo.documento && !validar(novo.documento)}
                    helperText={!!novo.documento && !validar(novo.documento) ? invalidMsg : ''}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={novo.valor}
                    onChange={(e) => handleInputChange(secaoIndex, tipo, 'valor', e.target.value)}
                    placeholder="0,00"
                  />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleClearFields(secaoIndex, tipo)}
                      disabled={!novo.nomeRazao && !novo.documento && !novo.valor}
                    >
                      Limpar
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleAddProduto(secaoIndex, tipo)}
                      disabled={!novo.nomeRazao || !validar(novo.documento) || novo.valorNumerico <= 0}
                    >
                      Adicionar
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
              {produtosList.map((produto) => (
                <TableRow key={produto.id}>
                  <TableCell>{produto.nomeRazao}</TableCell>
                  <TableCell>{produto.documento}</TableCell>
                  <TableCell>{produto.valor}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => handleRemoveProduto(secaoIndex, tipo, produto.id)}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

export default SectionTable;
